import type { StaffMember } from '@manaro/contracts'
import { formatDate, formatRelative } from '@/shared/lib/format.ts'
import { Badge } from '@/shared/ui/badge.tsx'
import { Button } from '@/shared/ui/button.tsx'
import {
  CardActions,
  CardFact,
  CardFacts,
  CardHeading,
  DesktopTable,
  MobileCard,
  MobileCardList,
} from '@/shared/ui/responsive-table.tsx'
import { staffStatusView } from '../labels.ts'

const COLUMNS = ['Сотрудник', 'Статус', 'Последний вход', 'Добавлен', '']

interface StaffTableProps {
  members: StaffMember[]
  onReissue: (member: StaffMember) => void
  onDisable: (member: StaffMember) => void
  onEnable: (member: StaffMember) => void
}

type StaffActionsProps = Omit<StaffTableProps, 'members'> & { member: StaffMember }

const lastLogin = (member: StaffMember): string =>
  member.lastLoginAt ? formatRelative(member.lastLoginAt) : 'ещё не входил'

function InviteDeadline({ member }: { member: StaffMember }) {
  const live = member.status === 'invited' && staffStatusView(member).tone === 'info'
  const expiresAt = live ? member.inviteExpiresAt : null
  if (!expiresAt) return null
  return <div className="mt-1 text-[12px] whitespace-nowrap text-ink-muted">ссылка до {formatDate(expiresAt)}</div>
}

function StatusBadge({ member }: { member: StaffMember }) {
  const view = staffStatusView(member)
  return <Badge tone={view.tone}>{view.label}</Badge>
}

/** The table keeps «Новая ссылка» quiet; on a card both buttons need a visible outline to read as buttons. */
function StaffActions({
  member,
  onReissue,
  onDisable,
  onEnable,
  reissueVariant,
}: StaffActionsProps & { reissueVariant: 'ghost' | 'secondary' }) {
  return (
    <>
      <Button variant={reissueVariant} size="sm" onClick={() => onReissue(member)}>
        Новая ссылка
      </Button>
      {member.status === 'disabled' ? (
        <Button variant="secondary" size="sm" onClick={() => onEnable(member)}>
          Включить
        </Button>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => onDisable(member)}>
          Отключить
        </Button>
      )}
    </>
  )
}

function StaffCard({ member, ...actions }: StaffActionsProps) {
  return (
    <MobileCard>
      <CardHeading aside={<StatusBadge member={member} />}>
        <div className="truncate text-[15px] font-semibold">{member.name}</div>
        <div className="mt-0.5 truncate text-[12px] text-ink-muted">{member.email}</div>
        <InviteDeadline member={member} />
      </CardHeading>

      <CardFacts>
        <CardFact label="Последний вход">{lastLogin(member)}</CardFact>
        <CardFact label="Добавлен">
          <div>{formatDate(member.createdAt)}</div>
          <div className="mt-0.5 truncate text-[12px] text-ink-muted">{member.createdBy}</div>
        </CardFact>
      </CardFacts>

      <CardActions>
        <StaffActions member={member} reissueVariant="secondary" {...actions} />
      </CardActions>
    </MobileCard>
  )
}

export function StaffTable({ members, ...actions }: StaffTableProps) {
  return (
    <>
      <MobileCardList label="Сотрудники">
        {members.map((member) => (
          <StaffCard key={member.id} member={member} {...actions} />
        ))}
      </MobileCardList>

      <DesktopTable>
        <table className="w-full min-w-[720px] text-left text-[13.5px]">
          <thead className="border-b border-line-soft text-[12px] text-ink-muted">
            <tr>
              {COLUMNS.map((title, index) => (
                <th key={index} scope="col" className="px-3.5 py-3 font-medium whitespace-nowrap">
                  {title || <span className="sr-only">Действия</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line-soft align-top">
            {members.map((member) => (
              <tr key={member.id}>
                <td className="max-w-[280px] px-3.5 py-3">
                  <div className="truncate font-medium">{member.name}</div>
                  <div className="mt-0.5 truncate text-[12px] text-ink-muted" title={member.email}>
                    {member.email}
                  </div>
                </td>
                <td className="px-3.5 py-3">
                  <StatusBadge member={member} />
                  <InviteDeadline member={member} />
                </td>
                <td className="px-3.5 py-3 whitespace-nowrap text-ink-muted">{lastLogin(member)}</td>
                <td className="px-3.5 py-3 text-ink-muted">
                  <div className="whitespace-nowrap">{formatDate(member.createdAt)}</div>
                  <div className="mt-0.5 truncate text-[12px]" title={member.createdBy}>
                    {member.createdBy}
                  </div>
                </td>
                <td className="px-3.5 py-2.5">
                  <div className="flex justify-end gap-1.5 whitespace-nowrap">
                    <StaffActions member={member} reissueVariant="ghost" {...actions} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DesktopTable>
    </>
  )
}
